import {memo, useEffect, useMemo, useState} from "react";
import {
    Input,
    Popover,
    PopoverHandler,
    PopoverContent,
} from "@material-tailwind/react";
import {PREVENT_OUTSIDE_CLICK_ATTR} from "../hooks/useComponentVisible";

interface TimePickerProps {
    value: string;
    onChange: (time: string) => void;
    label: string;
    placeholder?: string;
    className?: string;
}

const padTime = (value: number) => value.toString().padStart(2, "0");

const parseTimeValue = (rawValue: string): {hour: number | null; minute: number | null} => {
    if (!rawValue) {
        return {hour: null, minute: null};
    }
    const [h = "", m = ""] = rawValue.split(":");
    const parsedHour = parseInt(h, 10);
    const parsedMinute = parseInt(m, 10);
    const hour = !Number.isNaN(parsedHour) && parsedHour >= 0 && parsedHour < 24 ? parsedHour : null;
    const minute = !Number.isNaN(parsedMinute) && parsedMinute >= 0 && parsedMinute < 60 ? parsedMinute : null;

    return {hour, minute};
};

const TimePicker = ({value, onChange, label, placeholder, className}: TimePickerProps) => {
    const [selectedHour, setSelectedHour] = useState<number | null>(null);
    const [selectedMinute, setSelectedMinute] = useState<number | null>(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const parsedValue = useMemo(() => parseTimeValue(value), [value]);

    useEffect(() => {
        if (!value) {
            setSelectedHour(null);
            setSelectedMinute(null);
            return;
        }
        setSelectedHour(parsedValue.hour);
        setSelectedMinute(parsedValue.minute);
    }, [parsedValue.hour, parsedValue.minute, value]);

    const hours = useMemo(() => Array.from({length: 24}, (_, index) => index), []);
    const minutes = useMemo(() => Array.from({length: 60}, (_, index) => index), []);

    const handleHourSelect = (hour: number) => {
        setSelectedHour(hour);
        const minute = selectedMinute ?? parsedValue.minute;
        if (minute !== null) {
            const formatted = `${padTime(hour)}:${padTime(minute)}`;
            onChange(formatted);
        }
    };

    const handleMinuteSelect = (minute: number) => {
        setSelectedMinute(minute);
        const hour = selectedHour ?? parsedValue.hour;
        if (hour !== null) {
            const formatted = `${padTime(hour)}:${padTime(minute)}`;
            onChange(formatted);
            setIsPopoverOpen(false);
        }
    };

    const renderOption = (value: number, isSelected: boolean, onClick: () => void) => (
        <button
            type="button"
            key={value}
            className={`rounded-md border text-sm py-2 transition-colors ${
                isSelected
                    ? "bg-black text-white border-black dark:bg-teal-500 dark:border-teal-500"
                    : "bg-white text-black border-gray-200 hover:border-gray-400 dark:bg-blue-gray-900 dark:text-gray-200 dark:border-blue-gray-700"
            }`}
            onClick={onClick}
        >
            {padTime(value)}
        </button>
    );

    return (
        <div className={`block md:max-w-xs md:inline-block mb-2 md:mb-auto ${className ?? ""}`}>
            <Popover placement="bottom" open={isPopoverOpen} handler={setIsPopoverOpen}>
                <PopoverHandler>
                    <Input
                        className="dark:text-gray-500"
                        crossOrigin=""
                        label={label}
                        placeholder={placeholder}
                        value={value}
                        readOnly
                        onChange={() => null}
                    />
                </PopoverHandler>
                <PopoverContent
                    className="bg-white dark:bg-blue-gray-900 dark:text-gray-500 dark:border-blue-gray-500 z-[100] w-72"
                    {...{[PREVENT_OUTSIDE_CLICK_ATTR]: true}}
                >
                    <div className="flex flex-col gap-4">
                        <div>
                            <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-2">Hours</p>
                            <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1">
                                {hours.map((hour) =>
                                    renderOption(hour, hour === selectedHour, () => handleHourSelect(hour))
                                )}
                            </div>
                        </div>
                        <div>
                            <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-2">Minutes</p>
                            <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1">
                                {minutes.map((minute) =>
                                    renderOption(minute, minute === selectedMinute, () => handleMinuteSelect(minute))
                                )}
                            </div>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default memo(TimePicker);
